import React, { useImperativeHandle, useMemo, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { sql, StandardSQL, PostgreSQL } from '@codemirror/lang-sql';
import { CompletionContext, CompletionResult, snippetCompletion } from '@codemirror/autocomplete';
import { EditorView, Decoration, DecorationSet } from '@codemirror/view';
import { StateField, StateEffect } from '@codemirror/state';
import { diffLines } from 'diff';
import { McLoadingIndicator, McIcon } from '@maersk-global/mds-react-wrapper';
import { useSqlSchema, SqlSchemaFormat } from '../../hooks/useSqlSchema';
import { DatabaseType } from '../../utils/terminalConfig';
import { getSqlSyntaxTemplates } from '../../config/sqlSyntaxTemplates';

// SQL keywords that must not be interpreted as table aliases
const SQL_KEYWORDS = new Set([
    'on', 'where', 'inner', 'left', 'right', 'outer', 'cross', 'full', 'set', 'as',
    'and', 'or', 'not', 'select', 'group', 'order', 'by', 'having', 'limit', 'offset',
    'union', 'all', 'distinct', 'into', 'update', 'delete', 'insert', 'values', 'with',
    'case', 'when', 'then', 'else', 'end', 'null', 'true', 'false', 'between', 'in',
    'is', 'like', 'exists', 'any', 'some', 'over', 'partition', 'rows', 'range',
]);

/**
 * Scans the SQL text for all table alias definitions — including those inside CTEs,
 * subqueries, and multiple nested JOINs — then returns column completions when the
 * user types "alias." where alias resolves to a known schema table.
 */
function createAliasCompletionSource(lowerSchema: SqlSchemaFormat) {
    return (context: CompletionContext): CompletionResult | null => {
        const before = context.matchBefore(/\w+\./);
        if (!before) return null;

        const alias = before.text.slice(0, -1).toLowerCase();
        if (SQL_KEYWORDS.has(alias)) return null;

        const fullText = context.state.doc.toString();
        const aliasMap: Record<string, string> = {};

        // Matches: FROM/JOIN [schema.]table_name [AS] alias
        const fromJoinPattern = /(?:from|join)\s+(?:\w+\.)?([\w$]+)\s+(?:as\s+)?([\w$]+)/gi;
        let m: RegExpExecArray | null;
        while ((m = fromJoinPattern.exec(fullText)) !== null) {
            const tbl = m[1].toLowerCase();
            const al = m[2].toLowerCase();
            if (!SQL_KEYWORDS.has(al) && !SQL_KEYWORDS.has(tbl)) aliasMap[al] = tbl;
        }

        // Matches explicit: something AS alias (covers CTE names and inline views)
        const asPattern = /([\w$]+)\s+as\s+([\w$]+)/gi;
        while ((m = asPattern.exec(fullText)) !== null) {
            const tbl = m[1].toLowerCase();
            const al = m[2].toLowerCase();
            if (!SQL_KEYWORDS.has(al) && !SQL_KEYWORDS.has(tbl)) aliasMap[al] = tbl;
        }

        const resolvedTable = aliasMap[alias];
        if (!resolvedTable) return null;

        const schemaKey = Object.keys(lowerSchema).find(k => k.toLowerCase() === resolvedTable);
        if (!schemaKey) return null;

        const columns = lowerSchema[schemaKey];
        if (!columns || columns.length === 0) return null;

        return {
            from: before.from + alias.length + 1, // position right after the dot
            options: (columns as any[]).map(col => ({
                label: typeof col === 'string' ? col : col.label,
                type: 'property' as const,
            })),
            validFor: /^\w*$/,
        };
    };
}

function createSyntaxTemplateCompletionSource(databaseType: DatabaseType, externalDdlEnabled: boolean) {
    const templates = getSqlSyntaxTemplates(databaseType, { externalDdlEnabled }).map((template) =>
        snippetCompletion(template.apply, {
            label: template.label,
            detail: template.detail,
            type: template.type,
            boost: 99,
        })
    );

    const isPostgres = String(databaseType).includes('POSTGRES');
    const dataTypes = isPostgres
        ? [
            'BIGINT',
            'INTEGER',
            'NUMERIC(12,2)',
            'VARCHAR(255)',
            'TEXT',
            'DATE',
            'TIMESTAMP',
            'BOOLEAN',
            'UUID',
          ]
        : [
            'NUMBER',
            'NUMBER(12,2)',
            'VARCHAR2(255)',
            'CHAR(1)',
            'CLOB',
            'DATE',
            'TIMESTAMP',
          ];

    const dataTypeOptions = dataTypes.map((typeName) =>
        snippetCompletion(typeName, {
            label: typeName,
            detail: isPostgres ? 'PostgreSQL type' : 'Oracle type',
            type: 'keyword',
            boost: 120,
        })
    );

    const commandPrefixes = ['cr', 'al', 'dr', 'tr', 'gr', 're', 'in', 'up', 'de'];

    const isColumnTypeContext = (textBeforeCursor: string, lineBeforeCursor: string): boolean => {
        const lowerBefore = textBeforeCursor.toLowerCase();
        const inCreateTable = /create\s+table[\s\S]*\([\s\S]*$/i.test(lowerBefore);
        const inAlterAddColumn = /alter\s+table[\s\S]*add\s+column[\s\S]*$/i.test(lowerBefore);

        if (!inCreateTable && !inAlterAddColumn) return false;

        const trimmedLine = lineBeforeCursor.trimStart();
        // Example: "column_name " or "column_name var"
        const createTableColumnLine = /^"?[\w$]+"?\s+[\w()]*$/i.test(trimmedLine);
        // Example: "ADD COLUMN column_name " or "ADD COLUMN column_name var"
        const alterAddColumnLine = /^add\s+column\s+"?[\w$]+"?\s+[\w()]*$/i.test(trimmedLine);

        return createTableColumnLine || alterAddColumnLine;
    };

    return (context: CompletionContext): CompletionResult | null => {
        const token = context.matchBefore(/\w*/);
        if (!token) return null;

        const word = token.text.toLowerCase();
        const textBeforeCursor = context.state.sliceDoc(0, context.pos);
        const lineBeforeCursor = textBeforeCursor.split('\n').pop() || '';

        // Context-aware: when defining a column type, prioritize dialect-specific type completions.
        if (isColumnTypeContext(textBeforeCursor, lineBeforeCursor)) {
            const options = dataTypeOptions.filter((option) => {
                const label = option.label.toLowerCase();
                return word.length === 0 || label.startsWith(word);
            });

            if (options.length === 0) return null;

            return {
                from: token.from,
                options,
                validFor: /^\w*$/,
            };
        }

        const atStatementStart = /^\s*\w*$/.test(lineBeforeCursor);
        const isCommandPrefix = commandPrefixes.some((prefix) => word.startsWith(prefix));
        const isCommandWord = word === 'create' || word === 'alter' || word === 'grant' || word === 'revoke';

        // Outside command context, let built-in SQL completions handle Ctrl+Space.
        if (!isCommandPrefix && !isCommandWord && !atStatementStart) {
            return null;
        }

        if (!context.explicit && !isCommandPrefix && !isCommandWord) {
            return null;
        }

        const options = templates.filter((option) => {
            const label = option.label.toLowerCase();
            return word.length === 0 || label.includes(word);
        });

        if (options.length === 0) return null;

        return {
            from: token.from,
            options,
            validFor: /^\w*$/,
        };
    };
}

interface SqlEditorProps {
    value: string;
    onChange: (value: string) => void;
    terminalId: string;
    databaseType: DatabaseType;
    label?: string;
    required?: boolean;
    minHeight?: string;
    maxHeight?: string;
    readOnly?: boolean;
    externalDdlEnabled?: boolean;
    externalConnectionServerType?: 'POSTGRESQL' | 'ORACLE';
    /** Base (unoverridden) SQL — when set, differences are highlighted in the editor */
    baseContent?: string;
    /** When true renders the amber ⚡ {terminalId} override badge above the editor */
    isOverride?: boolean;
}

/** Imperative API exposed via `ref` so callers can drive the editor (e.g. Schema Explorer insertion). */
export interface SqlEditorHandle {
    /** Inserts `text` at the current cursor; replaces selection if any. */
    insertAtCursor: (text: string) => void;
    /** Returns the current selection `{ from, to, text }` (or zero-length cursor). */
    getSelection: () => { from: number; to: number; text: string };
    /** Focuses the editor. */
    focus: () => void;
}

export const SqlEditor = React.forwardRef<SqlEditorHandle, SqlEditorProps>(function SqlEditor({
    value,
    onChange,
    terminalId,
    databaseType,
    label = "SQL Content",
    required = false,
    minHeight = "300px",
    maxHeight = "600px",
    readOnly = false,
    externalDdlEnabled = false,
    externalConnectionServerType,
    baseContent,
    isOverride = false,
}, ref) {
    const viewRef = useRef<EditorView | null>(null);

    useImperativeHandle(ref, () => ({
        insertAtCursor(text: string) {
            const view = viewRef.current;
            if (!view) return;
            const { from, to } = view.state.selection.main;
            view.dispatch({
                changes: { from, to, insert: text },
                selection: { anchor: from + text.length },
            });
            view.focus();
        },
        getSelection() {
            const view = viewRef.current;
            if (!view) return { from: 0, to: 0, text: '' };
            const { from, to } = view.state.selection.main;
            return { from, to, text: view.state.sliceDoc(from, to) };
        },
        focus() {
            viewRef.current?.focus();
        },
    }), []);

    const { schema, isLoading, loadingStatus, error, refreshSchema } = useSqlSchema(terminalId, databaseType);

    const effectiveDatabaseType: DatabaseType =
        databaseType === 'EXTERNAL' && externalConnectionServerType
            ? externalConnectionServerType
            : databaseType;

    // CodeMirror's standard SQL dialect wraps uppercase table names in double quotes,
    // assuming they are case-sensitive identifiers. For Oracle, we can use lowercase 
    // names to prevent quoting, as Oracle parses unquoted lowercase identical to uppercase.
    const sqlExtension = useMemo(() => {
        const isPostgres = String(effectiveDatabaseType).includes('POSTGRES');
        const dialect = isPostgres ? PostgreSQL : StandardSQL;

        // Create a lowercase version of the schema just for CodeMirror
        const lowerSchema: SqlSchemaFormat = {};
        if (schema) {
            for (const [table, cols] of Object.entries(schema)) {
                if (Array.isArray(cols)) {
                    const isObjArr = cols.length > 0 && typeof cols[0] === 'object';
                    if (isObjArr) {
                        lowerSchema[table.toLowerCase()] = cols.map((c: any) => ({ ...c, label: c.label.toLowerCase() }));
                    } else {
                        lowerSchema[table.toLowerCase()] = cols.map((c: any) => c.toLowerCase());
                    }
                }
            }
        }

        const sqlLang = sql({ dialect, schema: lowerSchema });
        const aliasCompletionSource = createAliasCompletionSource(lowerSchema);
        const syntaxTemplateSource = createSyntaxTemplateCompletionSource(effectiveDatabaseType, externalDdlEnabled);

        // Register alias-aware completions into the SQL language data facet so they
        // run alongside (not instead of) the built-in keyword/table completions.
        // This makes "alias." suggestions work inside CTEs, subqueries, and nested JOINs.
        const aliasExt = sqlLang.language.data.of({
            autocomplete: (context: CompletionContext) => {
                const aliasResult = aliasCompletionSource(context);
                if (aliasResult) {
                    return aliasResult;
                }
                return syntaxTemplateSource(context);
            },
        });

        return [sqlLang, aliasExt];
    }, [effectiveDatabaseType, schema, externalDdlEnabled]);

    // Fix for CodeMirror autocomplete options height being squished by MDS global styles
    const customTheme = useMemo(() => {
        return EditorView.theme({
            ".cm-tooltip-autocomplete": {
                border: "1px solid var(--mds-color-neutral-weak-decoration, #ccc) !important",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1) !important",
                backgroundColor: "var(--mds-color-white-background, #fff) !important",
                minWidth: "200px !important",
            },
            ".cm-tooltip-autocomplete > ul": {
                maxHeight: "250px !important",
                overflowY: "auto !important",
                fontFamily: '"JetBrains Mono", "Monaco", "Menlo", monospace',
            },
            ".cm-tooltip-autocomplete > ul > li": {
                lineHeight: "1.5 !important",
                padding: "4px 12px !important",
                height: "auto !important",
                minHeight: "28px !important",
                display: "flex !important",
                alignItems: "center !important",
                fontSize: "13px !important",
                color: "var(--mds-color-neutral-strong-text, #333) !important",
            },
            ".cm-tooltip-autocomplete > ul > li[aria-selected=\"true\"]": {
                backgroundColor: "var(--mds-color-primary-main-background, #0073ab) !important",
                color: "#ffffff !important",
            },
            ".cm-completionLabel": {
                display: "inline-block !important",
                lineHeight: "normal !important",
            },
            ".cm-completionMatchedText": {
                fontWeight: "bold !important",
                textDecoration: "underline !important",
            }
        });
    }, []);

    // Diff decoration extension — highlights lines that differ from the base SQL.
    // Added lines: soft green background. Removed lines (present in base, absent here): soft red.
    const diffExtension = useMemo(() => {
        if (!baseContent) return [];

        const addedMark = Decoration.line({ attributes: { style: 'background: rgba(34,197,94,0.15); border-left: 3px solid rgba(34,197,94,0.7);' } });
        const removedMark = Decoration.line({ attributes: { style: 'background: rgba(239,68,68,0.12); border-left: 3px solid rgba(239,68,68,0.6);' } });

        const buildDecorations = (doc: any): DecorationSet => {
            // Use a Map keyed by line.from to deduplicate decorations at the same position.
            // Added marks take priority over removed marks so a changed line shows green.
            const decorationMap = new Map<number, any>();

            try {
                const changes = diffLines(baseContent ?? '', value ?? '');
                let lineNum = 1;

                // First pass: mark added lines (green)
                for (const part of changes) {
                    const count = part.count ?? 0;
                    if (part.removed) {
                        // Removed lines don't exist in the current doc — don't advance lineNum
                        continue;
                    }
                    if (part.added) {
                        for (let i = 0; i < count; i++) {
                            try {
                                const line = doc.line(lineNum + i);
                                decorationMap.set(line.from, addedMark.range(line.from));
                            } catch (_) { /* line out of bounds — skip */ }
                        }
                    }
                    lineNum += count;
                }

                // Second pass: mark lines adjacent to removed blocks (red), but don't
                // overwrite a position already marked as added.
                lineNum = 1;
                for (const part of changes) {
                    const count = part.count ?? 0;
                    if (part.removed) {
                        const targetLine = Math.min(lineNum, doc.lines);
                        try {
                            const line = doc.line(targetLine);
                            if (!decorationMap.has(line.from)) {
                                decorationMap.set(line.from, removedMark.range(line.from));
                            }
                        } catch (_) { /* line out of bounds — skip */ }
                    } else if (part.added) {
                        lineNum += count;
                    } else {
                        lineNum += count;
                    }
                }
            } catch (_) {
                // If diff computation itself throws (e.g. extremely large SQL), return no decorations
                return Decoration.none;
            }

            if (decorationMap.size === 0) return Decoration.none;

            // Decoration.set requires the array to be sorted by `from` position
            const sorted = Array.from(decorationMap.values()).sort((a, b) => a.from - b.from);
            return Decoration.set(sorted, true);
        };

        const diffField = StateField.define<DecorationSet>({
            create: (state) => buildDecorations(state.doc),
            update: (deco, tr) => tr.docChanged ? buildDecorations(tr.newDoc) : deco,
            provide: (f) => EditorView.decorations.from(f),
        });

        return [diffField];
    }, [baseContent, value]);

    return (
        <div className="mds-form-field" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label className="mds-text--small-bold">
                    {label} {required && <span style={{ color: 'var(--mds-color-error-main-background)' }}>*</span>}
                </label>
                {isOverride && terminalId && (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#f59e0b',
                        color: '#ffffff',
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        padding: '2px 8px',
                        borderRadius: '999px',
                        lineHeight: 1.4,
                        userSelect: 'none',
                    }}>
                        ⚡ {terminalId}
                    </span>
                )}
            </div>

            <div
                style={{
                    border: '1px solid var(--mds-color-neutral-weak-decoration)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--mds-color-white-background)',
                    position: 'relative'
                }}
            >
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        backdropFilter: 'blur(2px)',
                        borderRadius: '4px'
                    }}>
                        <McLoadingIndicator fit="medium" />
                        <span className="mds-text--small-bold" style={{ color: 'var(--mds-color-neutral-strong-text, #333)' }}>
                            {loadingStatus || 'Loading Schema...'}
                        </span>
                    </div>
                )}
                <CodeMirror
                    value={value}
                    minHeight={minHeight}
                    maxHeight={maxHeight}
                    extensions={[sqlExtension, customTheme, ...diffExtension]}
                    onChange={onChange}
                    onCreateEditor={(view) => { viewRef.current = view; }}
                    readOnly={readOnly || isLoading}
                    basicSetup={{
                        lineNumbers: true,
                        highlightActiveLineGutter: true,
                        foldGutter: true,
                        dropCursor: true,
                        allowMultipleSelections: true,
                        indentOnInput: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        autocompletion: true,
                        rectangularSelection: true,
                        crosshairCursor: true,
                        highlightActiveLine: true,
                        highlightSelectionMatches: true,
                        closeBracketsKeymap: true,
                        defaultKeymap: true,
                        searchKeymap: true,
                        historyKeymap: true,
                        foldKeymap: true,
                        completionKeymap: true,
                        lintKeymap: true,
                    }}
                    theme="light" // Can be switched to dark mode if MDS supports a standard dark variable structure
                    style={{ fontSize: '14px', fontFamily: '"JetBrains Mono", "Monaco", "Menlo", monospace' }}
                />

                {/* Status indicator for Schema loading */}
                <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    backgroundColor: 'var(--mds-color-white-background)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid var(--mds-color-neutral-weak-decoration)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    color: 'var(--mds-color-neutral-weak-text)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    zIndex: 10
                }}>
                    {isLoading ? (
                        <>
                            <McLoadingIndicator fit="small" />
                            <span>{loadingStatus || 'Loading schema...'}</span>
                        </>
                    ) : error ? (
                        <>
                            <McIcon icon="times-circle" style={{ color: 'var(--mds-color-warning-main-background)' }} />
                            <span title={error}>Schema load failed (No Autocomplete)</span>
                        </>
                    ) : Object.keys(schema).length > 0 ? (
                        <>
                            <McIcon icon="check-circle" style={{ color: 'var(--mds-color-success-main-background)' }} />
                            <span>Schema loaded ({Object.keys(schema).length} tables)</span>
                            <button
                                type="button"
                                onClick={refreshSchema}
                                title="Refresh Schema"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '2px 4px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'var(--mds-color-primary-main-background)',
                                    marginLeft: '4px'
                                }}
                            >
                                <McIcon icon="rotate" />
                            </button>
                        </>
                    ) : (
                        <>
                            <McIcon icon="shield" />
                            <span>Type query to begin</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
});
